import Modal from "@material-ui/core/Modal/Modal";
import { Text } from "components/Typography/text.styled";
import { FontSize } from "config/font";
import { TeamFormData } from "model/Company";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Api } from "services/api/api.service";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useOrganizationalStructureStore } from "web-apps/company/stores/organizationalStructureStore/organizationalStructureStore";
import { AlertModal } from "../general/alertModal/alertModal.component";
import { ButtonComponent } from "../general/button/button.component";
import { DropdownWithLabel } from "../general/dropDownWithLabel/dropdownWithLabel.component";
import { InfoModal } from "../general/infoModal/infoModal.component";
import { MultiSelectOption } from "../general/multiSelectWithLabel/multiSelectWithLabel.component";
import { TextInputWithLabel } from "../general/textInput/textInput.component";
import { TopModal } from "../general/topModal/topModal.component";
import {
  ActionContainer,
  OrgStructureModalBody,
} from "./organizationalStructure.styled";

type Props = {
  setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
  showEditModal: boolean;
  type: "Entity" | "Unit" | "Team";
  id?: number;
  parentId?: number;
  name: string;
};

export const normalizeNumber = (
  v: string | number | null | undefined
): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

type SettingKey = "GigPayDeduction" | "GigBreakTime";

export const EditModal: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const [state, organizationalStructureActions] =
    useOrganizationalStructureStore();
  const [, , alertDispatch] = useAlertStore();

  const isTeam = props.type === "Team";
  const isMoveBetweenUnits = isTeam && props.parentId === -1;

  const [name, setName] = useState(props.name);
  const [showInfo, setShowInfo] = useState(false);
  const [infoIsError, setInfoIsError] = useState(false);
  const [infoIsSaved, setInfoIsSaved] = useState(false);

  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showDeleteDeductionAlert, setShowDeleteDeductionAlert] =
    useState(false);

  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState<TeamFormData>({
    deduction: null,
    break: null,
  });
  const [original, setOriginal] = useState<TeamFormData>({
    deduction: null,
    break: null,
  });

  const [showDeduction, setShowDeduction] = useState(false);
  const [showBreak, setShowBreak] = useState(false);

  const [selectedUnit, setSelectedUnit] = useState<number | undefined>();

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const closeModal = useCallback(() => {
    props.setShowEditModal(false);
    setShowInfo(false);
    setInfoIsError(false);
    setInfoIsSaved(false);
    setLoading(false);
  }, [props]);

  useEffect(() => {
    if (!isTeam || !companyState.company?.id || !props.id) return;

    let cancelled = false;

    Api()
      .company.organizationalStructure.getWorkGroupSettings(companyState.company.id, props.id)
      .then((res) => {
        if (cancelled) return;

        const settings: { key: string; value: string | number | null }[] =
          res.data ?? [];

        const initDeduction = normalizeNumber(
          settings.find((s) => s.key === "GigPayDeduction")?.value
        );
        const initBreak = normalizeNumber(
          settings.find((s) => s.key === "GigBreakTime")?.value
        );

        setOriginal({ deduction: initDeduction, break: initBreak });
        setFormData({ deduction: initDeduction, break: initBreak });
        setShowDeduction(initDeduction !== null);
        setShowBreak(initBreak !== null);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [isTeam, companyState.company?.id, props.id]);

  const unitOptions: MultiSelectOption[] = useMemo(() => {
    return (
      state.structure?.entities?.flatMap((entity) =>
        entity.units
          ?.filter((u) => u.name !== "Unhandled")
          ?.map((u) => ({
            value: u.id.toString(),
            label: `${entity.name} - ${u.name}`,
          }))
      ) ?? []
    );
  }, [state.structure]);

  const changedSettings = useMemo((): {
    key: SettingKey;
    value: string | null;
  }[] => {
    if (!isTeam) return [];
    const diffs: { key: SettingKey; value: string | null }[] = [];

    if (formData.deduction !== original.deduction) {
      diffs.push({
        key: "GigPayDeduction",
        value: formData.deduction !== null ? String(formData.deduction) : null,
      });
    }
    if (formData.break !== original.break) {
      diffs.push({
        key: "GigBreakTime",
        value: formData.break !== null ? String(formData.break) : null,
      });
    }
    return diffs;
  }, [isTeam, formData, original]);

  const canSave = useMemo(() => {
    const nameChanged = name.trim() !== props.name.trim();
    const settingsChanged = changedSettings.length > 0;
    const unitChanged = isMoveBetweenUnits ? Boolean(selectedUnit) : true;
    return (
      !loading &&
      (nameChanged || settingsChanged || (isMoveBetweenUnits && unitChanged))
    );
  }, [
    loading,
    name,
    props.name,
    changedSettings,
    isMoveBetweenUnits,
    selectedUnit,
  ]);

  const showToast = useCallback(
    (ok: boolean) => {
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: ok ? "checkmark" : "",
          title: ok ? t("WorkGroup.Updated") : t("Alerts.OhNo"),
          message: ok
            ? t("WorkGroup.WorkGroupHasBeenUpdated")
            : t("Alerts.SomethingWentWrong"),
        },
      });
    },
    [alertDispatch, t]
  );

  const handleSaveChanges = useCallback(() => {
    if (!companyState.company?.id || !props.id) return;
    setLoading(true);

    let updatePromise: Promise<any>;

    if (props.type === "Team" && props.parentId) {
      updatePromise = organizationalStructureActions.updateTeam(
        companyState.company.id,
        props.id,
        name,
        isMoveBetweenUnits ? selectedUnit : undefined
      );
    } else if (props.type === "Unit" && props.parentId) {
      updatePromise = organizationalStructureActions.updateUnit(
        companyState.company.id,
        props.parentId,
        props.id,
        name
      );
    } else {
      updatePromise = organizationalStructureActions.updateEntity(
        companyState.company.id,
        props.id,
        name
      );
    }

    updatePromise
      .then(() => {
        if (isTeam && changedSettings.length > 0) {
          return Promise.all(
            changedSettings.map((s) =>
              Api().company.organizationalStructure.updateTeamSetting(
                companyState.company!.id,
                props.id!,
                s.key,
                s.value
              )
            )
          ).then(() => {
            showToast(true);
          });
        }
      })
      .then(() => {
        if (!mountedRef.current) return;
        setInfoIsSaved(true);
        setInfoIsError(false);
        setShowInfo(true);
        setLoading(false);
        props.setShowEditModal(false);
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setInfoIsSaved(false);
        setInfoIsError(true);
        setShowInfo(true);
        setLoading(false);
        showToast(false);
      });
  }, [
    companyState.company?.id,
    props.id,
    props.type,
    props.parentId,
    name,
    isMoveBetweenUnits,
    selectedUnit,
    isTeam,
    changedSettings,
    organizationalStructureActions,
    showToast,
    props,
  ]);

  const handleDelete = useCallback(() => {
    if (!companyState.company?.id || !props.id) return;
    setLoading(true);

    let deletePromise: Promise<any>;

    if (props.type === "Team" && props.parentId) {
      deletePromise = organizationalStructureActions.deleteTeam(
        companyState.company.id,
        props.parentId,
        props.id
      );
    } else if (props.type === "Unit" && props.parentId) {
      deletePromise = organizationalStructureActions.deleteUnit(
        companyState.company.id,
        props.parentId,
        props.id
      );
    } else {
      deletePromise = organizationalStructureActions.deleteEntity(
        companyState.company.id,
        props.id
      );
    }

    deletePromise
      .then(() => {
        if (!mountedRef.current) return;
        setInfoIsSaved(false);
        setInfoIsError(false);
        setShowInfo(true);
        setLoading(false);
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setInfoIsSaved(false);
        setInfoIsError(true);
        setShowInfo(true);
        setLoading(false);
      });
  }, [
    companyState.company?.id,
    props.id,
    props.parentId,
    props.type,
    organizationalStructureActions,
  ]);

  return (
    <>
      <Modal open={props.showEditModal} onClose={closeModal}>
        <OrgStructureModalBody
          style={{ height: "auto", width: 700, paddingBottom: 20 }}
        >
          <TopModal
            title={t(`OrganizationalStructure.Edit`, { name: props.name })}
            setClose={closeModal}
            marginLeft={30}
          />

          <Text style={{ width: 500, margin: "0 auto", marginLeft: 30 }}>
            {t(`OrganizationalStructure.Create${props.type}Info`)}
          </Text>

          <TextInputWithLabel
            label={t("WorkGroup.Name")}
            value={name}
            fontSize={FontSize.Standard}
            onChange={(e: any) => setName(e.currentTarget.value)}
            marginLeft={30}
            marginTop={20}
            maxLength={100}
            marginBottom={10}
          />
          {isMoveBetweenUnits && (
            <DropdownWithLabel
              label={t("OrganizationalStructure.Unit")}
              options={unitOptions}
              value={selectedUnit?.toString()}
              onChange={(value) => setSelectedUnit(parseInt(value))}
              placeholder={t("OrganizationalStructure.ChooseUnitToMoveTo")}
            />
          )}

          <ActionContainer style={{ margin: "0 auto" }}>
            <ButtonComponent
              size={"small"}
              onClick={closeModal}
              marginTop={10}
              marginBottom={20}
              marginRight={10}
            >
              {t("OrganizationalStructure.Cancel")}
            </ButtonComponent>

            <ButtonComponent
              size={"small"}
              onClick={() => setShowDeleteAlert(true)}
              marginTop={10}
              marginBottom={20}
              marginRight={10}
            >
              {t(`OrganizationalStructure.Remove`, {
                type: t(`OrganizationalStructure.${props.type}`).toLowerCase(),
              })}
            </ButtonComponent>

            <ButtonComponent
              size={"small"}
              onClick={handleSaveChanges}
              action={true}
              marginTop={10}
              marginBottom={20}
              disabled={!canSave}
            >
              {t("OrganizationalStructure.SaveChanges")}
            </ButtonComponent>
          </ActionContainer>
        </OrgStructureModalBody>
      </Modal>

      {showInfo && (
        <InfoModal
          isOpen={showInfo}
          title={
            infoIsError
              ? t("OrganizationalStructure.SomethingWentWrong")
              : infoIsSaved
              ? t("OrganizationalStructure.Saved")
              : t("OrganizationalStructure.Removed")
          }
          message={
            infoIsError
              ? t("OrganizationalStructure.SomethingWentWrongInfo")
              : infoIsSaved
              ? t(`OrganizationalStructure.YourChangesHaveBeenSaved`)
              : t(`OrganizationalStructure.SuccessfullyRemoved`, {
                  name: props.name,
                })
          }
          confirmText={t("OrganizationalStructure.Ok")}
          onConfirm={closeModal}
          onCancel={closeModal}
        />
      )}

      {showDeleteAlert && (
        <AlertModal
          isOpen={showDeleteAlert}
          title={t(`OrganizationalStructure.AlertTitle`, { name: props.name })}
          message={t(`OrganizationalStructure.AlertInfo-${props.type}`, {
            name: props.name,
          })}
          onConfirm={() => {
            handleDelete();
            setShowDeleteAlert(false);
          }}
          onCancel={() => setShowDeleteAlert(false)}
          confirmText={t("OrganizationalStructure.Confirm")}
          cancelText={t("OrganizationalStructure.Cancel")}
        />
      )}

      {showDeleteDeductionAlert && (
        <AlertModal
          isOpen={showDeleteDeductionAlert}
          title={t(`Alerts.AlertDeductionTitle`, { name: props.name })}
          message={t(`Alerts.AlertDeductionInfo`, { name: props.name })}
          onConfirm={() => {
            setFormData((prev) => ({ ...prev, deduction: null }));
            setShowDeduction(false);
            setShowDeleteDeductionAlert(false);
          }}
          onCancel={() => setShowDeleteDeductionAlert(false)}
          confirmText={t("OrganizationalStructure.Confirm")}
          cancelText={t("OrganizationalStructure.Cancel")}
        />
      )}
    </>
  );
};
